import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { EVENTS, isKnownEvent } from './events';

/**
 * Flow's own anonymous usage analytics (they replace Google Analytics): the events go to the sandbox gateway
 * (`<vfs path>/api/v1/analytics/collect`), which keeps counters and shows them in Flow admin › Analytics.
 *
 *  - First party, no cookie, no identifier in the browser: who is counted (once a day) is worked out by the gateway from the
 *    session, as a hash that changes every day and is thrown away. What the user typed is never sent: parameters are choices,
 *    counts and buckets, and anything that looks like free text or a secret is dropped.
 *  - "Do Not Track" / Global Privacy Control count as no.
 *  - Pages are reported by route template (`/code/:tenant/:project/:service/:branch/:tab`), so ids never reach the counters.
 *  - Events are sent in small batches (every few seconds, and when the page is hidden); a failed send is dropped, never retried
 *    in a loop, and never shown to the user.
 */

const DEBUG_KEY = 'flow.analytics.debug';
const MAX_VALUE_LENGTH = 100;
const MAX_BATCH = 20;
const FLUSH_MS = 5000;
const DROPPED_KEYS = /(prompt|text|message|content|email|mail|name|token|secret|password|path|url|file)$/i;

let started = false;
let defaultParams = {surface: 'web'};
const queue = [];
let timer = null;

const safeStorage = {
  get: (key) => { try { return localStorage.getItem(key); } catch (_error) { return null; } },
};

const collectUrl = () => `${String(config.vfsServiceUrl || '').replace(/\/+$/, '')}/api/v1/analytics/collect`;
export const isConfigured = () => Boolean(config.vfsServiceUrl);
const privacySignal = () => typeof navigator !== 'undefined' && (navigator.doNotTrack === '1' || navigator.globalPrivacyControl === true);
export const isEnabled = () => isConfigured() && !privacySignal();
const debugging = () => Boolean(import.meta.env?.DEV) || safeStorage.get(DEBUG_KEY) === '1';

/** What can be put in a parameter: short strings, numbers, booleans. Free text and anything secret-looking is left out. */
export const sanitizeParams = (params = {}) => {
  const clean = {};
  Object.entries(params).forEach(([key, value]) => {
    if (DROPPED_KEYS.test(key)) { return; }
    if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) {
      clean[key] = value;
    } else if (typeof value === 'string' && value) {
      clean[key] = value.length > MAX_VALUE_LENGTH ? value.slice(0, MAX_VALUE_LENGTH) : value;
    }
  });
  return clean;
};

/** A route as a template: ids are never part of what is reported. */
export const templatePath = (pathname = '/') => {
  const segments = pathname.split('/').filter(Boolean);
  const looksLikeId = (segment) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) || (segment.length > 20 && /\d/.test(segment));
  if (segments[0] === 'code') {
    const names = ['code', ':tenant', ':project', ':service', ':branch', ':tab'];
    return `/${segments.map((segment, index) => (index < names.length ? names[index] : ':rest')).slice(0, 6).join('/')}`;
  }
  return `/${segments.map((segment) => (looksLikeId(segment) ? ':id' : segment)).join('/')}`.replace(/\/$/, '') || '/';
};

const flush = () => {
  if (timer) { clearTimeout(timer); timer = null; }
  if (queue.length === 0 || !isEnabled()) { queue.length = 0; return; }
  const events = queue.splice(0, MAX_BATCH);
  try {
    // keepalive: the last batch still leaves when the page is closed.
    Promise.resolve(getAuthorizedFetch()(collectUrl(), {
      method: 'POST',
      headers: {'content-type': 'application/json'},
      body: JSON.stringify({events}),
      keepalive: true,
    })).catch(() => {});
  } catch (_error) { /* analytics never breaks the app */ }
  if (queue.length > 0) { flush(); }
};

/** Sends what is waiting now (the batching does it every few seconds and when the page is hidden). */
export const flushAnalytics = () => flush();

const enqueue = (name, params) => {
  if (!isEnabled()) { return; }
  queue.push({name, params});
  if (queue.length >= MAX_BATCH) { flush(); } else if (!timer) { timer = setTimeout(flush, FLUSH_MS); }
};

/** Starts the batching: sends what is waiting when the page is hidden or closed. */
export const initAnalytics = () => {
  if (started || typeof document === 'undefined') { return; }
  started = true;
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') { flush(); } });
  window.addEventListener('pagehide', flush);
};

export const trackPage = (pathname) => {
  const page = templatePath(pathname);
  if (debugging()) { console.debug('[analytics] page_view', page); } // eslint-disable-line no-console
  enqueue('page_view', sanitizeParams({...defaultParams, page}));
};

/** Parameters every event carries, unless the event sets its own (the web app is `surface: 'web'`). */
export const setDefaultParams = (params = {}) => { defaultParams = {...defaultParams, ...params}; };

export const track = (name, params = {}) => {
  if (!isKnownEvent(name)) {
    if (debugging()) { console.warn('[analytics] unknown event ignored:', name); } // eslint-disable-line no-console
    return;
  }
  const clean = sanitizeParams({...defaultParams, ...params});
  if (debugging()) { console.debug('[analytics]', name, clean); } // eslint-disable-line no-console
  enqueue(name, clean);
};

/** A bucket instead of a raw duration: easy to chart, and nothing identifying. */
export const durationBucket = (ms) => {
  const seconds = ms / 1000;
  if (seconds < 10) { return '<10s'; }
  if (seconds < 30) { return '10-30s'; }
  if (seconds < 120) { return '30-120s'; }
  if (seconds < 600) { return '2-10m'; }
  return '>10m';
};

/** How long something took to happen (e.g. from asking for an app to its first preview). */
export const timeBucket = (ms) => {
  const minutes = ms / 60_000;
  if (minutes < 1) { return '<1m'; }
  if (minutes < 3) { return '1-3m'; }
  if (minutes < 10) { return '3-10m'; }
  if (minutes < 30) { return '10-30m'; }
  return '>30m';
};

/** How many agent turns. */
export const turnsBucket = (count) => {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n === 0) { return '0'; }
  if (n <= 2) { return String(n); }
  if (n <= 5) { return '3-5'; }
  if (n <= 10) { return '6-10'; }
  return '>10';
};

/** How many calendar days. */
export const daysBucket = (days) => {
  const n = Math.max(0, Math.floor(Number(days) || 0));
  if (n === 0) { return '0'; }
  if (n === 1) { return '1'; }
  if (n <= 7) { return '2-7'; }
  if (n <= 30) { return '8-30'; }
  return '>30';
};

/**
 * The older call sites in the app (`window.analytics.trackAction/trackDeployment/trackFeature`) report UI clicks, which Flow no
 * longer measures: they stay callable so nothing breaks, and send nothing.
 */
export const installLegacyShim = () => {
  if (typeof window === 'undefined') { return; }
  const nothing = () => {};
  window.analytics = {trackAction: nothing, trackDeployment: nothing, trackFeature: nothing};
};

/** For tests only: forget the batching state of this page. */
export const resetAnalyticsForTests = () => { started = false; queue.length = 0; if (timer) { clearTimeout(timer); timer = null; } defaultParams = {surface: 'web'}; };

export { EVENTS };
